import React, { type CSSProperties, useMemo, useState } from 'react'
import { Collapse, Tooltip } from '@mui/material'

import { Chip, Icon, Typography } from '~components/internal'
import { type AgentTraceNode, type SourceDisplayLink } from '~types'

import { SourceLink } from '../MessageBubble'

import './AgentTracePanel.scss'

interface AgentTracePanelProps {
  /** The spans of a single orchestration run, i.e. one user query. */
  nodes: AgentTraceNode[]
}

type TraceRow = {
  node: AgentTraceNode
  depth: number
  /** Sub-agent rows fold up the tools they ran; every other row is always expanded. */
  isCollapsible: boolean
}

const formatDuration = (ms: number | null | undefined): string | null => {
  if (ms == null) return null
  if (ms < 1000) return `${ms}ms`
  if (ms < 60_000) return `${(ms / 1000).toFixed(2)}s`
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ${Math.round((ms % 60_000) / 1000)}s`
  if (ms < 86_400_000) return `${(ms / 3_600_000).toFixed(1)}h`
  return `${(ms / 86_400_000).toFixed(1)}d`
}

const formatTokens = (tokens: number | null | undefined): string | null =>
  tokens == null || tokens === 0 ? null : tokens.toLocaleString()

const startMs = (node: AgentTraceNode): number =>
  node.started_at ? new Date(node.started_at).getTime() : 0

const endMs = (node: AgentTraceNode): number => startMs(node) + (node.duration_ms ?? 0)

type Window = { start: number; end: number }

const windowOf = (nodes: AgentTraceNode[]): Window => {
  const timed = nodes.filter((node) => node.started_at)
  if (timed.length === 0) return { start: 0, end: 1 }
  return {
    start: Math.min(...timed.map(startMs)),
    end: Math.max(...timed.map(endMs)),
  }
}

/** Group spans into one trace per user query by provider trace id, inherited by tool rows from their invoking call; rows with no traced ancestor are left out. */
export const splitTraceByQuery = (traceNodes: AgentTraceNode[]): AgentTraceNode[][] => {
  const byId = new Map(traceNodes.map((node) => [node.id, node]))
  const inheritedTraceId = (node: AgentTraceNode): string | null => {
    const seen = new Set<string>()
    let current: AgentTraceNode | undefined = node
    while (current && !seen.has(current.id)) {
      if (current.trace_id) return current.trace_id
      seen.add(current.id)
      current = current.parent_id ? byId.get(current.parent_id) : undefined
    }
    return null
  }

  const byTraceId = new Map<string, AgentTraceNode[]>()
  for (const node of traceNodes) {
    const traceId = inheritedTraceId(node)
    if (traceId) byTraceId.set(traceId, [...(byTraceId.get(traceId) ?? []), node])
  }

  return [...byTraceId.values()].sort((a, b) => windowOf(a).start - windowOf(b).start)
}

/** Depth-first order so children sit under the call that made them. */
const flattenTrace = (nodes: AgentTraceNode[]): TraceRow[] => {
  const childrenByParent = new Map<string | null, AgentTraceNode[]>()
  for (const node of nodes) {
    const key = node.parent_id ?? null
    childrenByParent.set(key, [...(childrenByParent.get(key) ?? []), node])
  }
  for (const siblings of childrenByParent.values()) {
    siblings.sort((a, b) => startMs(a) - startMs(b))
  }

  const knownIds = new Set(nodes.map((node) => node.id))
  const rows: TraceRow[] = []
  const walk = (node: AgentTraceNode, depth: number): void => {
    const children = childrenByParent.get(node.id) ?? []
    rows.push({ node, depth, isCollapsible: children.length > 0 && isSubAgent(node) })
    for (const child of children) {
      walk(child, depth + 1)
    }
  }
  // Anything whose parent was not captured is treated as a root.
  for (const node of nodes) {
    if (!node.parent_id || !knownIds.has(node.parent_id)) {
      walk(node, 0)
    }
  }
  return rows
}

/** Mirrors the "Sources Used" list on chat messages so the two look the same. */
const SelectedSourcesList: React.FC<{ sources: SourceDisplayLink[] }> = ({ sources }) => {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <div className='agent-trace-panel__selected-sources'>
      <button
        className='agent-trace-panel__selected-sources-toggle'
        onClick={() => setIsOpen((prev) => !prev)}
        type='button'
      >
        <Typography variant='caption'>{`Selected Sources (${sources.length})`}</Typography>
        <Icon name={isOpen ? 'ExpandLess' : 'ExpandMore'} fontSize='small' />
      </button>
      <Collapse in={isOpen}>
        <div className='agent-trace-panel__selected-sources-list'>
          {sources.map((source, index) => (
            <div
              className='agent-trace-panel__selected-source-row'
              key={`${source.source_id}-${index}`}
            >
              <div className='agent-trace-panel__selected-source-link'>
                <SourceLink orgAiSettings={null} source={source} linkToInternalPageSource={false} />
              </div>
            </div>
          ))}
        </div>
      </Collapse>
    </div>
  )
}

const TraceRowDetails: React.FC<{ node: AgentTraceNode }> = ({ node }) => (
  <div className='agent-trace-panel__details'>
    {node.node_type === 'llm_call' && (
      <div className='agent-trace-panel__stats'>
        <span>{`input ${node.total_input_tokens ?? 0} (cached ${node.cached_input_tokens ?? 0})`}</span>
        <span>{`output ${node.output_tokens ?? 0} (reasoning ${node.reasoning_tokens ?? 0})`}</span>
        {node.turn_index != null && <span>{`turn ${node.turn_index}`}</span>}
        {node.time_to_first_token_ms != null && (
          <span>{`first token ${formatDuration(node.time_to_first_token_ms)}`}</span>
        )}
      </div>
    )}
    {node.error_message && (
      <pre className='agent-trace-panel__payload agent-trace-panel__payload--error'>
        {node.error_message}
      </pre>
    )}
    {node.output_text && (
      <>
        <Typography variant='caption'>Output</Typography>
        <pre className='agent-trace-panel__payload'>{node.output_text}</pre>
      </>
    )}
    {node.selected_sources && node.selected_sources.length > 0 && (
      <SelectedSourcesList sources={node.selected_sources as SourceDisplayLink[]} />
    )}
    {node.params_json && (
      <>
        <Typography variant='caption'>Params</Typography>
        <pre className='agent-trace-panel__payload'>
          {JSON.stringify(node.params_json, null, 2)}
        </pre>
      </>
    )}
    {node.result_json && Object.keys(node.result_json).length > 0 && (
      <>
        <Typography variant='caption'>Result</Typography>
        <pre className='agent-trace-panel__payload'>
          {JSON.stringify(node.result_json, null, 2)}
        </pre>
      </>
    )}
  </div>
)

/** Delegation rows are named after the invoked agent-as-tool (e.g. `retrieve_information`); name them after the sub-agent they hand off to instead. */
const isSubAgent = (node: AgentTraceNode): boolean =>
  node.node_type === 'llm_call' && !!node.parent_id?.startsWith('tool:')

const subAgentNames = (nodes: AgentTraceNode[]): Map<string, string> => {
  const names = new Map<string, string>()
  for (const node of nodes) {
    if (node.node_type === 'llm_call' && node.parent_id?.startsWith('tool:') && node.label) {
      names.set(node.parent_id, node.label)
    }
  }
  return names
}

const sumTokens = (nodes: AgentTraceNode[], field: keyof AgentTraceNode): number =>
  nodes.reduce((total, node) => {
    const value = node[field]
    return total + (typeof value === 'number' ? value : 0)
  }, 0)

const AgentTracePanel: React.FC<AgentTracePanelProps> = ({ nodes }) => {
  const [isOpen, setIsOpen] = useState(true)
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())

  const rows = useMemo(() => flattenTrace(nodes), [nodes])
  const traceWindow = useMemo(() => windowOf(nodes), [nodes])
  const delegatedAgents = useMemo(() => subAgentNames(nodes), [nodes])

  // A sub-agent's tools stay hidden until its row is expanded, so a hand-off reads as one line until opened.
  const visibleRows = useMemo(() => {
    const byId = new Map(nodes.map((node) => [node.id, node]))
    const collapsedIds = new Set(
      rows
        .filter(({ node, isCollapsible }) => isCollapsible && !expandedIds.has(node.id))
        .map(({ node }) => node.id),
    )
    const isVisible = (node: AgentTraceNode): boolean => {
      const seen = new Set<string>([node.id])
      let parent = node.parent_id ? byId.get(node.parent_id) : undefined
      while (parent && !seen.has(parent.id)) {
        if (collapsedIds.has(parent.id)) return false
        seen.add(parent.id)
        parent = parent.parent_id ? byId.get(parent.parent_id) : undefined
      }
      return true
    }
    return rows.filter(({ node }) => isVisible(node))
  }, [rows, nodes, expandedIds])

  const toggleRow = (id: string): void =>
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const summaryChips: string[] = []
  const addTokenChip = (tokens: number, suffix: string): void => {
    const formatted = formatTokens(tokens)
    if (formatted) summaryChips.push(`${formatted} ${suffix}`)
  }
  addTokenChip(sumTokens(nodes, 'total_input_tokens'), 'in')
  addTokenChip(sumTokens(nodes, 'cached_input_tokens'), 'cached')
  addTokenChip(sumTokens(nodes, 'output_tokens'), 'out')
  addTokenChip(sumTokens(nodes, 'reasoning_tokens'), 'reasoning')
  const durationLabel = formatDuration(traceWindow.end - traceWindow.start)
  if (durationLabel) summaryChips.push(durationLabel)

  return (
    <div className='agent-trace-panel'>
      <button
        className='agent-trace-panel__header'
        onClick={() => setIsOpen((prev) => !prev)}
        type='button'
      >
        <div className='agent-trace-panel__header-left'>
          <Icon name='AccountTree' fontSize='small' />
          <Typography variant='body2' className='agent-trace-panel__title'>
            Agent Trace
          </Typography>
          {summaryChips.map((chip) => (
            <Chip key={chip} label={chip} size='small' />
          ))}
        </div>
        <Icon name={isOpen ? 'ExpandLess' : 'ExpandMore'} fontSize='small' />
      </button>

      <Collapse in={isOpen}>
        <div className='agent-trace-panel__body'>
          {visibleRows.map(({ node, depth, isCollapsible }) => {
            const span = Math.max(traceWindow.end - traceWindow.start, 1)
            const offsetPct = ((startMs(node) - traceWindow.start) / span) * 100
            const widthPct = Math.max(((node.duration_ms ?? 0) / span) * 100, 0.5)
            const firstTokenOffsetPct =
              node.time_to_first_token_ms != null
                ? ((startMs(node) - traceWindow.start + node.time_to_first_token_ms) / span) * 100
                : null
            const isLlmCall = node.node_type === 'llm_call'
            const isExpanded = expandedIds.has(node.id)
            const subAgent = node.is_agent_delegation ? delegatedAgents.get(node.id) : undefined

            return (
              <div className='agent-trace-panel__row' key={node.id}>
                <button
                  className='agent-trace-panel__row-header'
                  onClick={() => toggleRow(node.id)}
                  type='button'
                >
                  <div
                    className='agent-trace-panel__row-label'
                    style={{ '--depth': depth } as CSSProperties}
                  >
                    {isCollapsible ? (
                      <Icon
                        name={isExpanded ? 'ExpandLess' : 'ExpandMore'}
                        fontSize='small'
                        className='agent-trace-panel__row-caret'
                      />
                    ) : (
                      <span className='agent-trace-panel__row-caret' />
                    )}
                    <Icon
                      name={
                        isLlmCall
                          ? 'AutoAwesome'
                          : node.is_agent_delegation
                            ? 'AccountTree'
                            : 'BuildOutlined'
                      }
                      fontSize='small'
                    />
                    <Typography variant='caption' className='agent-trace-panel__row-name'>
                      {subAgent ? `Hands off to ${subAgent}` : node.label}
                    </Typography>
                    {isLlmCall && node.model && (
                      <Tooltip title={node.model}>
                        <Typography variant='caption' className='agent-trace-panel__row-model'>
                          {node.model}
                        </Typography>
                      </Tooltip>
                    )}
                  </div>
                  <div className='agent-trace-panel__row-track'>
                    <div
                      className={`agent-trace-panel__bar agent-trace-panel__bar--${node.node_type}${
                        node.status === 'failed' ? ' agent-trace-panel__bar--failed' : ''
                      }`}
                      style={{ left: `${offsetPct}%`, width: `${widthPct}%` }}
                    />
                    {firstTokenOffsetPct != null && (
                      <div
                        className='agent-trace-panel__first-token-marker'
                        style={{ left: `${firstTokenOffsetPct}%` }}
                        title={`First token: ${formatDuration(node.time_to_first_token_ms)}`}
                      />
                    )}
                  </div>
                  <Typography variant='caption' className='agent-trace-panel__row-duration'>
                    {formatDuration(node.duration_ms) ?? '—'}
                  </Typography>
                </button>
                <Collapse in={isExpanded}>
                  <TraceRowDetails node={node} />
                </Collapse>
              </div>
            )
          })}
        </div>
      </Collapse>
    </div>
  )
}

export default AgentTracePanel
